'use client';

import { c as _c } from "react/compiler-runtime";
import './accordion-group.scss';
import * as RdxAccordion from '@radix-ui/react-accordion';
import type { ElementType, ReactNode } from 'react';
import React, { type JSX } from 'react';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { trackClick } from '@sola/tracking/lib/analytics/events.ts';
import { getTextFromReactNode } from '@sola/utils/react.ts';
import { Badge, type BadgeProps } from '../../atoms/badge/badge.tsx';
export interface AccordionItemProps {
  header: string;
  badge?: BadgeProps;
  seoLevel?: number;
  children: JSX.Element;
}
export interface AccordionGroupProps {
  items?: AccordionItemProps[];
  variant?: 'default' | 'tabbed';
  onChange?: (index: number) => void;
}
interface AccordionGroupElementProps {
  seoLevel?: number;
  children: ReactNode;
}
const AccordionTrigger: React.FC<AccordionGroupElementProps & {
  ref?: React.Ref<HTMLButtonElement>;
}> = t0 => {
  const $ = _c(10);
  const {
    seoLevel,
    children,
    ref
  } = t0;
  const Comp = (seoLevel ? `h${seoLevel}` : "div") as ElementType;
  let t1;
  if ($[0] !== children) {
    t1 = () => {
      trackClick(getTextFromReactNode(children), undefined, "AccordionGroup__trigger");
    };
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Comp || $[3] !== children) {
    t2 = <Comp className="label-potent label-xl">{children}</Comp>;
    $[2] = Comp;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArkIcon iconName="chevron-down" className="AccordionGroup__chevron px-snug-sm ml-comfy-xl" />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== ref || $[7] !== t1 || $[8] !== t2) {
    t4 = <RdxAccordion.Header className="AccordionGroup__header" asChild={true}><div><RdxAccordion.Trigger className="AccordionGroup__trigger flex align-center justify-between w-full" ref={ref} onClick={t1}>{t2}{t3}</RdxAccordion.Trigger></div></RdxAccordion.Header>;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const AccordionContent: React.FC<AccordionGroupElementProps & {
  ref?: React.Ref<HTMLDivElement>;
}> = t0 => {
  const $ = _c(3);
  const {
    children,
    ref
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== ref) {
    t1 = <RdxAccordion.Content className="AccordionGroup__content body-sm" ref={ref}>{children}</RdxAccordion.Content>;
    $[0] = children;
    $[1] = ref;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
export const AccordionGroup: React.FC<AccordionGroupProps> = t0 => {
  const $ = _c(9);
  const {
    items,
    variant: t1,
    onChange
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const t2 = `AccordionGroup AccordionGroup--${variant} w-full flex flex-col gap-comfy-sm`;
  const t3 = `0_${items?.[0]?.header}`;
  let t4;
  if ($[0] !== onChange) {
    t4 = value => {
      if (!value || !onChange) {
        return;
      }
      const index = value.split("_")[0];
      onChange(Number(index));
    };
    $[0] = onChange;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== items) {
    t5 = items?.map(_temp);
    $[2] = items;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = <RdxAccordion.Root className={t2} type="single" defaultValue={t3} onValueChange={t4} collapsible={true}>{t5}</RdxAccordion.Root>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
};
function _temp(item, index_0) {
  return <RdxAccordion.Item key={`${index_0}_${item.header}`} className="AccordionGroup__item flex flex-col" value={`${index_0}_${item.header}`}><AccordionTrigger seoLevel={item.seoLevel}><div className="flex gap-comfy-sm"><span>{item.header}</span>{item.badge && <Badge {...item.badge} />}</div></AccordionTrigger><AccordionContent>{item.children}</AccordionContent></RdxAccordion.Item>;
}